import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  HbrOption,
  HbrSelect,
  HbrSpinner,
  HbrTextarea,
} from '@harbor/elements-react-standalone';
import { check, downloadSimple } from '@harbor/elements/icons';
import { fetchStream } from '../../support/queries';
import { apiPostJson } from '../../support/aiApi';
import { AIContentRecord } from '../../types/aiTypes';
import {
  buildAIContentSessionId,
  fetchAIResponse,
  submitAIFeedback,
  updateAIContentMessage,
} from '../../support/aiHelpers';
import StbButton from '../security-toolbox-ui/StbButton';
import StbModal from '../security-toolbox-ui/StbModal';
import StbMarkdown from '../security-toolbox-ui/StbMarkdown';
import PdfExport from './PdfExport';

interface AIContentProps {
  title?: string;
  related_type: string;
  related_id: string;
  question_id: number;
  product_name?: string;
  height?: number | string;
  width?: number | string;
  context?: string;
  conversation?: string[] | string | any;
  conversationRequired?: boolean;
  item?: any;
  regenerate?: boolean;
  pdfTitle?: string;

  model?: string;
  model_name?: string;
  rag_pipeline_group_id?: number | null;
  use_rag?: boolean;
  use_drift?: boolean;

  /**
   * Opt into server-side grounding via the modular context builder. When enabled and
   * `related_type` is a known reference entity (campaign, assessment, customer,
   * opportunity, product, architecture), toolbox_ai prepends that entity's relational
   * context to the prompt. `relations` selects specific edges; otherwise the whole
   * declared graph is included.
   */
  entityContext?: {
    enabled?: boolean;
    relations?: string[];
    includeAll?: boolean;
    /**
     * Optional grounding-root override. By default the backend grounds on this component's
     * (relatedType, relatedId) — which is also the saved-response key. Set these to ground on a
     * DIFFERENT entity without changing the saved-response row, e.g. keep one stable report row
     * while grounding migrates from `customer` to `opportunity` once an opportunity is linked.
     */
    referenceType?: string;
    referenceId?: string | number;
  };
}

type SseEventName = 'status' | 'token' | 'final' | 'error' | 'done' | 'message' | 'ping';

const STREAM_FLUSH_MS = 50;
const EMPTY_CONVERSATION: string[] = [];

function normalizeConversation(value: any): string[] {
  if (value === undefined || value === null || value === '') {
    return [];
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item === undefined || item === null || item === '') {
          return '';
        }

        if (typeof item === 'string') {
          return item;
        }

        try {
          return JSON.stringify(item, null, 2);
        } catch {
          return String(item);
        }
      })
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    return value ? [value] : [];
  }

  try {
    return [JSON.stringify(value, null, 2)];
  } catch {
    return [String(value)];
  }
}

function valueOf<T = any>(...values: any[]): T | null {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') return value as T;
  }

  return null;
}

function extractChatText(data: any): string {
  return String(
    valueOf(
      data?.response,
      data?.response_text,
      data?.answer,
      data?.content,
      data?.message,
      data?.assistant_message?.content,
      data?.assistant_message?.response,
      data?.assistant?.content,
      data?.data?.response,
      data?.data?.answer,
      data?.data?.content,
      '',
    ) || '',
  );
}

function extractChatRag(data: any): any | null {
  return valueOf(
    data?.rag,
    data?.rag_context,
    data?.rag_chunks,
    data?.metadata?.rag,
    data?.metadata?.rag_context,
    data?.metadata?.rag_chunks,
    data?.retrieval?.rag,
    data?.retrieval?.context,
    data?.data?.rag,
    data?.data?.metadata?.rag,
    null,
  );
}

const AIContent: React.FC<AIContentProps> = ({
                                               title = '',
                                               related_type,
                                               related_id,
                                               question_id,
                                               product_name = '',
                                               height = '100%',
                                               width = '100%',
                                               context = '',
                                               conversation: rawConversation = EMPTY_CONVERSATION,
                                               conversationRequired = false,
                                               item = null,
                                               regenerate = false,
                                               pdfTitle = '',
                                               model,
                                               model_name,
                                               rag_pipeline_group_id = null,
                                               use_rag = true,
                                               use_drift = true,
                                               entityContext,
                                             }) => {
  const [aiContent, setAIContent] = useState<AIContentRecord | null>(null);
  const [responseText, setResponseText] = useState('');
  const [rag, setRag] = useState<any | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [additionalContext, setAdditionalContext] = useState('');

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackScore, setFeedbackScore] = useState<number | null>(null);

  const [isStreaming, setIsStreaming] = useState(false);
  const [streamStatus, setStreamStatus] = useState<string | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [showResponsePanel, setShowResponsePanel] = useState(false);

  const contentRef = useRef<HTMLDivElement | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const pendingTextRef = useRef('');
  const flushTimerRef = useRef<number | null>(null);
  const requestSeqRef = useRef(0);

  const sessionId = useMemo(
    () => buildAIContentSessionId(related_type, related_id, question_id),
    [related_type, related_id, question_id],
  );

  const conversation = useMemo(
    () => normalizeConversation(rawConversation),
    [rawConversation],
  );

  const conversationKey = useMemo(
    () => conversation.join('\n'),
    [conversation],
  );

  const itemKey = useMemo(() => {
    if (!item) return '';

    try {
      return JSON.stringify(item);
    } catch {
      return String(item);
    }
  }, [item]);

  const effectiveModelName = useMemo(
    () => model_name || model || undefined,
    [model_name, model],
  );

  const effectiveUseDrift = useMemo(
    () => use_drift ?? use_rag ?? true,
    [use_drift, use_rag],
  );

  const effectiveUseRag = useMemo(
    () => use_rag ?? effectiveUseDrift,
    [use_rag, effectiveUseDrift],
  );

  const effectiveRagProfileId = rag_pipeline_group_id ?? null;

  const effectivePdfTitle = useMemo(
    () => pdfTitle || `ai-content-${related_type}-${related_id}-${question_id}-${Date.now()}`,
    [pdfTitle, related_type, related_id, question_id],
  );

  /**
   * question_id addresses a row in ai_question, and the backend loads that
   * question text when the request carries no prompt (_resolve_prompt_text in
   * app/api/routes/chat.py). Sending `title` — or the 'Generate response'
   * placeholder for the callers that pass no title — made that the question the
   * model answered, so the stored question was never asked. `title` stays a
   * display heading; only use it as the prompt when there is no question_id.
   */
  const resolvePromptText = (fallbackPrompt: string) => {
    const numericQuestionId = Number(question_id);

    if (question_id !== undefined && question_id !== null && Number.isFinite(numericQuestionId)) {
      return '';
    }

    return title || fallbackPrompt;
  };

  const buildAIContentContext = (extraContext = '') => {
    let itemContext = '';

    if (item) {
      try {
        itemContext = `Item Context:\n${JSON.stringify(item, null, 2)}`;
      } catch {
        itemContext = `Item Context:\n${String(item)}`;
      }
    }

    return [
      product_name ? `Product: ${product_name}` : '',
      extraContext.trim(),
      context,
      itemContext,
      conversation.length ? `Conversation:\n${conversation.join('\n')}` : '',
    ]
      .filter(Boolean)
      .join('\n\n');
  };

  const buildModelAliases = () => {
    if (!effectiveModelName) return {};

    return {
      model: effectiveModelName,
      model_name: effectiveModelName,
      modelName: effectiveModelName,
      llm_model: effectiveModelName,
      llmModel: effectiveModelName,
    };
  };

  const buildChatPayload = (
    promptText: string,
    fullContext: string,
    { isRegenerate = regenerate }: { isRegenerate?: boolean } = {},
  ) => {
    return {
      session_id: sessionId,

      ...(promptText
        ? {
            message: promptText,
            prompt: promptText,
            question: promptText,
          }
        : {}),

      context: fullContext,
      prompt_context: fullContext,

      related_type,
      related_id,
      entity_type: related_type,
      entity_id: related_id,
      question_id,

      product: product_name || undefined,
      product_name: product_name || undefined,

      user_id: 'ui',

      retrieval: {
        drift: effectiveUseDrift,
        rag: effectiveUseRag,
        pipeline_group_id: effectiveRagProfileId ?? undefined,
        rag_profile_id: effectiveRagProfileId ?? undefined,
      },

      use_rag: effectiveUseRag,
      use_drift: effectiveUseDrift,
      useDrift: effectiveUseDrift,
      drift_enabled: effectiveUseDrift,

      rag_pipeline_group_id: effectiveRagProfileId ?? undefined,
      pipeline_group_id: effectiveRagProfileId ?? undefined,
      rag_profile_id: effectiveRagProfileId ?? undefined,

      ...buildModelAliases(),

      mcp: {
        enabled: false,
        server_ids: [],
        capability_names: [],
        force_refresh: false,
      },
      mcp_enabled: false,
      useMcp: false,
      use_mcp: false,

      metadata: {
        source: 'AIContent',
        related_type,
        related_id,
        entity_type: related_type,
        entity_id: related_id,
        question_id,
        product_name,
        regenerate: isRegenerate,
        ...(entityContext?.enabled
          ? {
              use_entity_context: true,
              ...(entityContext.relations
                ? { entity_context_relations: entityContext.relations }
                : {}),
              ...(entityContext.includeAll ? { entity_context_include_all: true } : {}),
              ...(entityContext.referenceType
                ? { entity_context_reference_type: entityContext.referenceType }
                : {}),
              ...(entityContext.referenceId !== undefined && entityContext.referenceId !== null
                ? { entity_context_reference_id: entityContext.referenceId }
                : {}),
            }
          : {}),
      },
    };
  };

  const normalizeChatRecord = (
    raw: any,
    fallbackResponse = '',
    fallbackRag: any | null = null,
  ): AIContentRecord => {
    const response = extractChatText(raw) || fallbackResponse;
    const nextRag = extractChatRag(raw) || fallbackRag;

    return {
      ...(raw || {}),
      id: raw?.id || raw?.message_id || raw?.assistant_message_id || sessionId,
      session_id: raw?.session_id || sessionId,
      conversation_id: raw?.conversation_id || raw?.conversation?.id || null,
      message_id:
        raw?.message_id ||
        raw?.assistant_message_id ||
        raw?.response_message_id ||
        raw?.assistant_message?.id ||
        raw?.id ||
        null,
      assistant_message_id:
        raw?.assistant_message_id ||
        raw?.message_id ||
        raw?.response_message_id ||
        raw?.assistant_message?.id ||
        raw?.id ||
        null,
      related_type,
      related_id,
      entity_type: related_type,
      entity_id: related_id,
      question_id,
      response,
      content: response,
      response_text: response,
      rag: nextRag,
      metadata: {
        ...(raw?.metadata || {}),
        source: 'AIContent',
        session_id: raw?.session_id || sessionId,
        conversation_id: raw?.conversation_id || raw?.conversation?.id || null,
        message_id:
          raw?.message_id ||
          raw?.assistant_message_id ||
          raw?.response_message_id ||
          raw?.assistant_message?.id ||
          raw?.id ||
          null,
        related_type,
        related_id,
        entity_type: related_type,
        entity_id: related_id,
        question_id,
        product_name,
        rag: nextRag || raw?.metadata?.rag || undefined,
      },
      updated_at: raw?.updated_at || raw?.created_at || new Date().toISOString(),
      created_at: raw?.created_at,
    } as AIContentRecord;
  };

  /**
   * POST /api/ai/chat/ is the only chat route (app/api/routes/chat.py).
   *
   * The former 'chat/' fallbacks resolved to /api/chat/, which no AI route
   * serves, so a real failure here was retried against an unrelated endpoint and
   * its body treated as a chat response. Failing straight through also keeps the
   * actual error message for the panel instead of the last fallback's.
   */
  const postChat = async (payload: Record<string, any>) => {
    const response = await apiPostJson<any>('ai/chat/', payload);

    if (!response) {
      throw new Error('The AI chat endpoint returned an empty response.');
    }

    return response;
  };

  const flushPendingText = () => {
    if (!pendingTextRef.current) return;

    const chunk = pendingTextRef.current;
    pendingTextRef.current = '';
    setResponseText((prev) => prev + chunk);
  };

  const scheduleFlush = () => {
    if (flushTimerRef.current !== null) return;

    flushTimerRef.current = window.setTimeout(() => {
      flushTimerRef.current = null;
      flushPendingText();
    }, STREAM_FLUSH_MS);
  };

  const resetStreamBuffers = () => {
    pendingTextRef.current = '';

    if (flushTimerRef.current !== null) {
      window.clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
  };

  useEffect(() => {
    const seq = requestSeqRef.current + 1;
    requestSeqRef.current = seq;

    const isCurrentRequest = () => requestSeqRef.current === seq;

    const fetchAIContent = async () => {
      setError(null);
      setIsInitialLoading(true);

      try {
        if (conversationRequired && !conversation.length) {
          if (!isCurrentRequest()) return;

          setAIContent(null);
          setResponseText('');
          setRag(null);
          setShowResponsePanel(false);
          return;
        }

        const promptText = resolvePromptText('Generate response');
        const fullContext = buildAIContentContext();

        if (!regenerate) {
          const existing = await fetchAIResponse(
            related_type,
            related_id,
            question_id,
            fullContext,
            {
              session_id: sessionId,
              model: effectiveModelName,
              model_name: effectiveModelName,
              use_rag: effectiveUseRag,
              use_drift: effectiveUseDrift,
              rag_pipeline_group_id: effectiveRagProfileId,
              pipeline_group_id: effectiveRagProfileId,
              rag_profile_id: effectiveRagProfileId,
              title,
              metadata: {
                source: 'AIContent',
                related_type,
                related_id,
                entity_type: related_type,
                entity_id: related_id,
                question_id,
                product_name,
              },
            },
          );

          if (!isCurrentRequest()) return;

          const existingText =
            existing?.response ||
            (existing as any)?.content ||
            (existing as any)?.response_text ||
            '';

          if (existing && existingText) {
            const nextRecord = normalizeChatRecord(
              existing,
              existingText,
              extractChatRag(existing) || (existing as any)?.rag || (existing as any)?.metadata?.rag || null,
            );

            setAIContent(nextRecord);
            setResponseText(
              nextRecord.response ||
              (nextRecord as any).content ||
              (nextRecord as any).response_text ||
              existingText,
            );
            setRag(
              nextRecord.rag ||
              (existing as any)?.rag ||
              (existing as any)?.metadata?.rag ||
              null,
            );
            setShowResponsePanel(true);
            return;
          }
        }

        const payload = buildChatPayload(promptText, fullContext);
        const chatResponse = await postChat(payload);

        if (!isCurrentRequest()) return;

        const nextRecord = normalizeChatRecord(chatResponse);

        setAIContent(nextRecord);
        setResponseText(nextRecord.response || '');
        setRag(nextRecord.rag || null);
        setShowResponsePanel(true);
      } catch (err: any) {
        if (!isCurrentRequest()) return;

        console.error('Error loading AI content:', err?.message);
        setError(err?.message || 'Failed to load AI response.');
      } finally {
        if (isCurrentRequest()) {
          setIsInitialLoading(false);
        }
      }
    };

    fetchAIContent();

    return () => {
      requestSeqRef.current += 1;
      abortControllerRef.current?.abort();
      resetStreamBuffers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sessionId,
    related_type,
    related_id,
    question_id,
    title,
    product_name,
    context,
    conversationKey,
    conversationRequired,
    itemKey,
    regenerate,
    effectiveModelName,
    effectiveUseRag,
    effectiveUseDrift,
    effectiveRagProfileId,
  ]);

  useEffect(() => {
    if (aiContent) {
      setResponseText(aiContent.response || (aiContent as any).content || '');
      setRag(aiContent.rag || (aiContent as any).metadata?.rag || null);
      setShowResponsePanel(true);
    }
  }, [aiContent]);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [responseText, isStreaming, streamStatus]);

  const ragChunks: any[] = useMemo(() => {
    const chunks = rag?.chunks;
    return Array.isArray(chunks) ? chunks : [];
  }, [rag]);

  const displayResponseText = useMemo(() => {
    return (
      responseText ||
      aiContent?.response ||
      (aiContent as any)?.content ||
      (aiContent as any)?.response_text ||
      ''
    );
  }, [responseText, aiContent]);

  const shouldShowResponsePanel = showResponsePanel || !!displayResponseText || isStreaming;

  const handleEditClick = () => setIsEditing(true);

  const handleSaveClick = async () => {
    if (!aiContent) return;

    try {
      const updated = await updateAIContentMessage(aiContent, responseText);
      setAIContent(updated);
      setResponseText(updated.response || responseText);
      setIsEditing(false);
    } catch (err: any) {
      console.error('Error saving AI content message:', err?.message);
      setError('Failed to save changes.');
    }
  };

  const handleCancelClick = () => {
    setResponseText(aiContent?.response || '');
    setIsEditing(false);
  };

  const parseSseFrames = (buffer: string) => {
    const normalized = buffer.replace(/\r\n/g, '\n');
    const frames: string[] = [];
    let remaining = normalized;
    let idx = remaining.indexOf('\n\n');

    while (idx >= 0) {
      frames.push(remaining.slice(0, idx));
      remaining = remaining.slice(idx + 2);
      idx = remaining.indexOf('\n\n');
    }

    return { frames, remaining };
  };

  const parseSseEvent = (raw: string): { eventName: SseEventName; data: any } | null => {
    let eventName: SseEventName = 'message';
    const dataLines: string[] = [];

    for (const line of raw.split('\n')) {
      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim() as SseEventName;
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trim());
      }
    }

    if (!dataLines.length) return null;

    const dataStr = dataLines.join('\n');

    try {
      return { eventName, data: JSON.parse(dataStr) };
    } catch {
      return null;
    }
  };

  const streamRegenerate = async (
    fullContext: string,
  ): Promise<{ finalText: string; finalRag: any | null; finalPayload: any | null }> => {
    setError(null);
    setIsStreaming(true);
    setShowResponsePanel(true);
    setStreamStatus('Starting...');
    setRag(null);
    setResponseText('');
    resetStreamBuffers();

    abortControllerRef.current?.abort();

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const promptText = resolvePromptText('Regenerate response');

    /**
     * Without this the backend takes the metadata.regenerate=false path in
     * _should_return_saved_ai_response and streams the previously saved answer
     * straight back, so Regenerate never generated anything.
     */
    const payload = buildChatPayload(promptText, fullContext, { isRegenerate: true });

    const resp = await fetchStream(
      'ai/chat/stream',
      payload,
      {
        signal: controller.signal,
      },
    );

    if (!resp.ok) {
      throw new Error(`Streaming request failed (${resp.status})`);
    }

    if (!resp.body) {
      throw new Error('Streaming response has no body');
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let finalText = '';
    let finalRag: any | null = null;
    let finalPayload: any | null = null;
    let streamDone = false;

    const handleEvent = (eventName: SseEventName, data: any) => {
      switch (eventName) {
        case 'status':
          setStreamStatus(data?.message || 'Generating...');
          return;

        case 'ping':
          return;

        case 'token': {
          const t = data?.text || data?.token || '';
          setStreamStatus(null);

          if (t) {
            finalText += t;
            pendingTextRef.current += t;
            scheduleFlush();
          }

          return;
        }

        case 'final': {
          flushPendingText();
          setStreamStatus(null);
          finalPayload = data;

          const finalResponse = extractChatText(data);

          if (finalResponse) {
            finalText = finalResponse;
            setResponseText(finalResponse);
          }

          const nextRag = extractChatRag(data);

          if (nextRag) {
            finalRag = nextRag;
            setRag(nextRag);
          }

          return;
        }

        case 'error':
          throw new Error(data?.message || data?.error || 'Generation failed');

        case 'done':
          streamDone = true;
          flushPendingText();
          setStreamStatus(null);
          return;

        default:
          return;
      }
    };

    try {
      while (!streamDone) {
        const r = await reader.read();
        if (r.done) break;

        buffer += decoder.decode(r.value, { stream: true });

        const { frames, remaining } = parseSseFrames(buffer);
        buffer = remaining;

        for (const f of frames) {
          const evt = parseSseEvent(f);
          if (!evt) continue;
          handleEvent(evt.eventName, evt.data);
        }
      }

      buffer += decoder.decode();

      if (buffer.trim()) {
        const evt = parseSseEvent(buffer.trim());
        if (evt) handleEvent(evt.eventName, evt.data);
      }

      flushPendingText();

      return { finalText, finalRag, finalPayload };
    } finally {
      try {
        reader.releaseLock();
      } catch {
        // no-op
      }

      flushPendingText();
      resetStreamBuffers();
      setIsStreaming(false);
      setStreamStatus(null);
      abortControllerRef.current = null;
    }
  };

  const handleRegenerate = () => setShowRegenerateModal(true);

  const handleSubmitRegenerate = async () => {
    const fullContext = buildAIContentContext(additionalContext);

    try {
      setShowRegenerateModal(false);

      const { finalText, finalRag, finalPayload } = await streamRegenerate(fullContext);

      const nextRecord = normalizeChatRecord(finalPayload, finalText, finalRag);

      setAIContent(nextRecord);
      setResponseText(nextRecord.response || finalText);
      setRag(nextRecord.rag || finalRag);
      setAdditionalContext('');
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        console.error('Error regenerating AI content:', err?.message);
        setError(err?.message || 'Failed to regenerate AI content.');
      }

      setIsStreaming(false);
      setStreamStatus(null);
      resetStreamBuffers();
    }
  };

  const handleFeedback = () => setShowFeedbackModal(true);

  const handleSubmitFeedback = async () => {
    try {
      await submitAIFeedback({
        related_type: 'ai-content',
        related_id: aiContent?.message_id
          ? String(aiContent.message_id)
          : sessionId,
        score: feedbackScore,
        text: feedbackText,
        source: 'AIContent',
        metadata: {
          session_id: sessionId,
          related_type,
          related_id,
          question_id,
          title,
          product_name,
          message_id: aiContent?.message_id || aiContent?.assistant_message_id || aiContent?.id || null,
        },
      });

      setShowFeedbackModal(false);
      setFeedbackText('');
      setFeedbackScore(null);
    } catch (err: any) {
      console.error('Error submitting feedback:', err?.message);
      setError('Failed to submit feedback.');
    }
  };

  if (error) {
    return <div className="text-red-600">{error}</div>;
  }

  if (isInitialLoading && !shouldShowResponsePanel) {
    return <HbrSpinner size="large">Loading...</HbrSpinner>;
  }

  if (conversationRequired && !conversation.length && !shouldShowResponsePanel) {
    return null;
  }

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="hbr-button-group">
          <StbButton
            onClick={handleRegenerate}
            className="hbr-button-group__button hbr-button-group__button--first"
            disabled={isStreaming || isInitialLoading}
          >
            {isStreaming ? 'Generating...' : 'Regenerate'}
          </StbButton>

          {isEditing ? (
            <>
              <StbButton
                role="default"
                icon={check}
                onClick={handleSaveClick}
                className="hbr-button-group__button hbr-button-group__button--inner"
                disabled={isStreaming || isInitialLoading}
              />
              <StbButton
                role="cancel"
                onClick={handleCancelClick}
                className="hbr-button-group__button hbr-button-group__button--last"
                disabled={isStreaming || isInitialLoading}
              />
            </>
          ) : (
            <StbButton
              role="update"
              onClick={handleEditClick}
              className="hbr-button-group__button hbr-button-group__button--last"
              disabled={isStreaming || isInitialLoading || !aiContent}
            />
          )}
        </div>

        <PdfExport
          content={displayResponseText}
          filename={effectivePdfTitle}
          ragChunks={ragChunks}
          buttonLabel=""
          buttonIcon={downloadSimple}
          isMarkdown={true}
          onExportComplete={() => console.log('PDF export completed successfully')}
          onExportError={(exportError) => {
            console.error('PDF export error:', exportError);
            setError(`PDF export failed: ${exportError.message}`);
          }}
        />
      </div>

      {shouldShowResponsePanel && (
        <>
          {isEditing ? (
            <HbrTextarea
              label={title}
              value={displayResponseText}
              onHbr-input={(e: any) => setResponseText(e.target.value)}
              rows={14}
            />
          ) : (
            <div
              ref={contentRef}
              style={{
                border: '1px solid #d0d7de',
                borderRadius: 8,
                padding: 12,
                height,
                width,
                overflowY: 'auto',
                minHeight: typeof height === 'number' ? height : 240,
                backgroundColor: '#fff',
              }}
            >
              {isStreaming ? (
                displayResponseText ? (
                  <div style={{ whiteSpace: 'pre-wrap' }}>{displayResponseText}</div>
                ) : (
                  <div className="text-sm text-gray-500">{streamStatus || 'Generating...'}</div>
                )
              ) : (
                <StbMarkdown>{displayResponseText}</StbMarkdown>
              )}
            </div>
          )}
        </>
      )}

      <StbButton role="request" onClick={handleFeedback} disabled={isStreaming || !aiContent || !displayResponseText}>
        Feedback
      </StbButton>

      <StbModal
        title="Add Additional Context"
        open={showRegenerateModal}
        submitLabel={isStreaming ? 'Generating...' : 'Add Context'}
        submitButtonRole="add"
        submitDisabled={isStreaming}
        onSubmit={async () => handleSubmitRegenerate()}
        onClose={async () => {
          if (!isStreaming) {
            setAdditionalContext('');
            setShowRegenerateModal(false);
          }
        }}
      >
        <HbrTextarea
          label="Context"
          value={additionalContext}
          onHbr-input={(e: any) => setAdditionalContext(e.target.value)}
        />
      </StbModal>

      <StbModal
        title="Provide Feedback"
        open={showFeedbackModal}
        submitLabel="Submit Feedback"
        submitButtonRole="request"
        submitDisabled={(feedbackText ?? '').trim() === '' && feedbackScore === null}
        onSubmit={async () => handleSubmitFeedback()}
        onClose={async () => {
          setShowFeedbackModal(false);
          setFeedbackText('');
          setFeedbackScore(null);
        }}
      >
        <HbrTextarea
          label="Feedback"
          value={feedbackText}
          onHbr-input={(e: any) => setFeedbackText(e.target.value)}
        />
        <HbrSelect
          label="Usefulness Score"
          onHbr-change={(e: any) => setFeedbackScore(Number(e.target.value))}
          value={feedbackScore ? String(feedbackScore) : ''}
          placeholder="Select a score"
        >
          {[1, 2, 3, 4, 5].map((score) => (
            <HbrOption key={score} value={String(score)}>
              {score}
            </HbrOption>
          ))}
        </HbrSelect>
      </StbModal>
    </div>
  );
};

export default AIContent;